import React, { useState } from "react";
import { intl } from "LocaleWrapper";

import { Card, FormGroup } from "@cofano/cck";

import { Box, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ReplayIcon from "@mui/icons-material/Replay";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCubes } from "@fortawesome/pro-duotone-svg-icons";

import { usePermission } from "imports/Utils";

import UpdateCharterContactsDialog from "view/charters/sections/UpdateCharterContactsDialog";
import CreateCharterContactDialog from "view/charters/sections/CreateCharterContactDialog";

import { ContactLink } from "utils/LinkUtils";

import { DetailsBlock, Height } from "../Blocks";

import { SalesCharter } from "types/index";

import { ContactRoleType } from "__generated__/graphql";
import { useMutation } from "@apollo/client";
import { RestoreDefaultContactsDocument } from "__generated__/view/charters/graphql/charter.mutations.generated";
import { useConfirm } from "components/hooks/useConfirm";

type SalesCharterContactsBlockProps = {
	charter: SalesCharter;
	color: string;
	refetch: () => void;
};

const SalesCharterContactsBlock = ({ charter, color, refetch }: SalesCharterContactsBlockProps) => {

	const { isReadOnly } = usePermission();
	const confirm = useConfirm();

	const [ updateContactsdialogOpen, openUpdateContactsDialog ] = useState(false);
	const [ createContactDialogOpen, openCreateContactDialog ] = useState(false);

	const [ restoreDefault ] = useMutation(RestoreDefaultContactsDocument);

	const restoreDefaultContacts = () => {
		restoreDefault({
			variables: {
				salesCharterId: charter.id,
			},
		}).then(() => {
			refetch();
		});
	};

	const contactRoles = Object.values(ContactRoleType).sort((a, b) => {
		const orderMap: Record<ContactRoleType, number> = {
			[ContactRoleType.OrderEntry]: 0,
			[ContactRoleType.NominationAndConfirmation]: 1,
			[ContactRoleType.CustomerUpdate]: 2,
			[ContactRoleType.CargoDocuments]: 3,
			[ContactRoleType.DemurrageApproval]: 4,
			[ContactRoleType.Invoice]: 5,
			[ContactRoleType.InvoiceReminder]: 6,
		};

		return orderMap[a] - orderMap[b];
	});


	return (
		<React.Fragment>
			<DetailsBlock height={Height.Auto}>
				<Card
					color={color}
					icon={<FontAwesomeIcon icon={faCubes} style={{ color: color, height: 22, width: 22 }} />}
					title={intl.formatMessage({ id: "CONTACTS" })}
					actions={[ {
						icon: <Tooltip title={intl.formatMessage({ id: "RESTORE_DEFAULT" })}>
							<ReplayIcon
								fontSize="small"
								onClick={() => {
									confirm({
										type: "WARNING",
										title: intl.formatMessage({ id: "RESTORE_DEFAULT_CONTACTS_CONFIRMATION" }),
									}).then(() => {
										restoreDefaultContacts();
									});
								}}
							/>
						</Tooltip>,
					}, {
						icon: <EditIcon fontSize="small" />,
						hidden: isReadOnly,
						onClick: () => openUpdateContactsDialog(true),
					}, {
						icon: <AddIcon fontSize="small" />,
						hidden: isReadOnly,
						onClick: () => openCreateContactDialog(true),
					} ]}
					disablePadding>
					<Box sx={{ borderTop: 1, borderColor: "divider", padding: 2 }}>
						<React.Fragment>
							{contactRoles.map(role => {

								const contacts = [ ...charter.contacts || [] ].filter(it => it.contactRoleType === role);

								return (
									<FormGroup
										label={intl.formatMessage({ id: role })}
										labelPlacement="left"
										labelWidth={300}
										labelPaddingTop={0}
										overflow="hidden">
										{contacts.length > 0 ? (
											<React.Fragment>
												{contacts.map((it, index) => {

													const contactFromBroker = charter.broker?.contacts?.find(c => c.id === it.contact.id);

													return (
														<div key={index}>
															<ContactLink
																relationId={contactFromBroker ? charter.broker?.id : charter.debtor.id}
																contact={it.contact} />
														</div>
													);
												})}
											</React.Fragment>
										) : "-"}
									</FormGroup>
								);
							})}
						</React.Fragment>
					</Box>
				</Card>
			</DetailsBlock>

			<UpdateCharterContactsDialog
				contactRoles={contactRoles}
				open={updateContactsdialogOpen}
				onClose={() => openUpdateContactsDialog(false)}
				charter={charter}
				refetch={refetch} />

			<CreateCharterContactDialog
				open={createContactDialogOpen}
				onClose={() => openCreateContactDialog(false)}
				charter={charter} />
		</React.Fragment>
	);
};

export default SalesCharterContactsBlock;
